// hooks/features/chatbot/use-chatbot-session.ts
import {
  initiateSession,
  sendChatMessage,
} from "@/src/services/api/chatbot/chatbot.service";
import { useRef, useState } from "react";

export type ChatMessage = Readonly<{
  id: string;
  role: "user" | "assistant";
  content: string;
  isStreaming?: boolean;
}>;

export const useChatbotSession = (widgetId: string) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const sessionRef = useRef<{
    session_id: string;
    token: string;
    expires_at: string;
  } | null>(null);

  const ensureSession = async () => {
    if (sessionRef.current) {
      const expiresAt = new Date(sessionRef.current.expires_at).getTime();
      if (Date.now() < expiresAt - 30000) return sessionRef.current;
    }
    const session = await initiateSession(widgetId);
    sessionRef.current = session;
    return session;
  };

  const sendMessage = async (text: string) => {
    if (!text.trim() || isLoading) return;
    setIsLoading(true);

    // add user message
    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: text,
    };
    setMessages((prev) => [...prev, userMsg]);

    // add empty assistant message for streaming
    const assistantId = `assistant-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { id: assistantId, role: "assistant", content: "", isStreaming: true },
    ]);

    try {
      const session = await ensureSession();
      await sendChatMessage(
        session.session_id,
        session.token,
        text,
        (chunk) => {
          setMessages((prev) =>
            prev.map((message) =>
              message.id === assistantId
                ? { ...message, content: message.content + chunk }
                : message,
            ),
          );
        },
      );
    } catch (e) {
      console.error("Chat failed:", e);
      setMessages((prev) =>
        prev.map((message) =>
          message.id === assistantId
            ? { ...message, isStreaming: false }
            : message,
        ),
      );
    } finally {
      setMessages((prev) =>
        prev.map((message) =>
          message.id === assistantId
            ? { ...message, isStreaming: false }
            : message,
        ),
      );
      setIsLoading(false);
    }
  };

  const clearMessages = () => setMessages([]);

  return { messages, isLoading, sendMessage, clearMessages };
};
